import { relativeTimeThreshold } from 'moment';
import Component from '../../Component';

export interface ToggleSwitchOption {
	value: string;
	label: string;
}

export interface ToggleSwitchOptions {
	leftOption: ToggleSwitchOption;
	rightOption: ToggleSwitchOption;
}

export interface ToggleSwitchState {
	currentValue: string;
}

class ToggleSwitch extends Component<HTMLElement, ToggleSwitchState> {
	static selector = '.toggle-switch';

	options?: ToggleSwitchOptions;

	constructor(element: HTMLElement, options: ToggleSwitchOptions) {
		super(element);
		this.options = options;
	}

	state = {
		currentValue: '',
	};

	static children = {
		labels: ['.toggle-switch__label'],
		toggle: '.toggle-switch__toggle',
	};

	/**
	 * Creates an instance of the switch-toggle with the rendered element.
	 *
	 * @param {ToggleSwitchOptions} options
	 * @return {ToggleSwitch} ToggleSwitch instance
	 */
	static create(options: ToggleSwitchOptions) {
		const element = document.createElement('div');
		element.className = 'toggle-switch';

		// Render children
		element.insertAdjacentHTML(
			'beforeend',
			[
				// Left option
				`
			<span class="toggle-switch__label" data-value="${options.leftOption.value}" tabindex="0">
				${options.leftOption.label}
			</span>
		`,
				// Toggle
				`
				<button class="toggle-switch__toggle"></button>
			`,
				// Right option
				`
			<span class="toggle-switch__label" data-value="${options.rightOption.value}" tabindex="0">
				${options.rightOption.label}
			</span>
		`,
			].join('\n')
		);

		return ToggleSwitch.initializeWithElement(element, options);
	}

	afterInit() {
		(this.children?.labels as HTMLElement[]).forEach((element) => {
			element.addEventListener('click', this.onLabelClick.bind(this));
		});

		this.children?.toggle.addEventListener(
			'click',
			this.onToggleClick.bind(this)
		);

		// Set left option as default value
		this.setState({
			currentValue: this.options?.leftOption.value,
		});
	}

	onLabelClick(event: MouseEvent) {
		event.preventDefault();

		this.setState({
			currentValue: (event.currentTarget as HTMLElement).getAttribute(
				'data-value'
			) as string,
		});
	}

	onToggleClick(event: MouseEvent) {
		event.preventDefault();

		const { currentValue } = this.state;
		const targetValueIndex =
			this.options?.rightOption.value === currentValue ? 0 : 1;
		const targetOptionValue =
			Object.values(this.options || {})[targetValueIndex]?.value || '';

		this.setState({ currentValue: targetOptionValue });
	}

	select(value: string) {
		this.setState({ currentValue: value });
	}

	componentDidUpdate() {
		const { currentValue } = this.state || {};

		this.children?.labels.forEach((element) => {
			const isSelected = element.dataset.value === currentValue;

			element.classList.toggle(
				'toggle-switch__label--active',
				isSelected
			);

			element.setAttribute(
				'aria-selected',
				isSelected ? 'true' : 'false'
			);

			element.setAttribute(
				'aria-label',
				isSelected
					? element.textContent || ''
					: `Click to switch to ${element.textContent}`
			);

			if (isSelected) {
				element.setAttribute('aria-selected', 'true');
			} else {
				element.removeAttribute('aria-selected');
			}
		});

		this.children?.toggle.setAttribute(
			'data-active-option',
			this.options?.leftOption.value === currentValue ? 'left' : 'right'
		);
	}
}

export default ToggleSwitch;
